"use client"

import { useMemo, useState, useEffect, useContext, useRef } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  alpha,
  Checkbox,
  Collapse,
  useMediaQuery,
  Drawer,
  Divider,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import CustomPagination from "./CustomPagination"

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility"
import ReplayIcon from "@mui/icons-material/Replay"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import DeleteIcon from "@mui/icons-material/Delete"
import BarChartIcon from "@mui/icons-material/BarChart"
import TuneIcon from "@mui/icons-material/Tune"
import FilterListIcon from "@mui/icons-material/FilterList"
import ChannelIcon from "@mui/icons-material/Tv"
import CloseIcon from "@mui/icons-material/Close"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import EventIcon from "@mui/icons-material/Event"
import BadgeIcon from "@mui/icons-material/Badge"
import PaymentIcon from "@mui/icons-material/Payment" // Importación nueva
import CreditCardIcon from "@mui/icons-material/CreditCard" // Importación nueva
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import InfoIcon from "@mui/icons-material/Info"
import EditIcon from "@mui/icons-material/Edit"

import { ThemeProvider, createTheme } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import "./TablaVentasComponent.css"
import { GridFooterContainer, GridFooter } from "@mui/x-data-grid"

import dayjs from "dayjs"
import jsPDF from "jspdf"
import "jspdf-autotable"

import ModalViewTablaVenta from "./ModalViewTablaVenta.js"
import ModalViewTablaDevolucion from "./ModalViewTablaDevolucion.js"
import GraficasVentasModal from "./graficas/graficas-ventas-modal"
import Swal from "sweetalert2"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "#fff",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            padding: "0 16px",
            height: "56px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "white !important",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255, 255, 255, 0.7) !important",
            "&:hover": {
              color: "white !important",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "white !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            padding: "0 16px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: "rgba(66, 153, 225, 0.08)",
              transition: "background 0.2s ease",
            },
            transition: "background 0.2s ease",
            cursor: "pointer",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(224, 224, 224, 0.5)",
            display: "none !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#a1a1a1",
            },
          },
        },
      },
    },
  },
});

const TablaVentasComponent = () => {
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItemID,
    generarFacturaPDF,
    getUsuario,
    getTransaccionTransformedVentas,
    getTransaccionTransformedVentasAllData, // NUEVO: para gráficas, esto utiliza el filtro de ventas pero no tiene en cuenta el paginado
    getEntidadByID,
    getItemById,
    getItemUbicacionFilter,
    generarRemitoPDF,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getTransaccionMontoTotalByTipoTransaccion,
    updateDeleteTransaccion,
    postTransaccionAuditoria,
    isNavbarCollapsed,
    getFechaHoraActualClient,
    getParametrosGlobales,
    getCanalEntidad,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    getCuentaCorrienteByIdEntidad, // ✅ Para obtener cuenta corriente del cliente
    getAndPostCajaDatos, // ✅ Para recalcular caja cuando se elimina una transacción
    user,
    tieneAccesoFuncionalidad, // 🔒 Permiso granular para eliminar venta (btnEliminarTransaccion)
    entidadAtributoNoEliminados,
    getEntidadAtributoNoEliminados,
    getEntidadAtributoClasificacionByIdAtributo,
  } = useContext(ErpContext)

  // ===== Responsive flags =====
  // "Laptop para abajo": aprox <= 1200px
  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // ===== Estados principales =====
  const [openModal, setOpenModal] = useState(false)
  const [transaccionItems, setTransaccionItems] = useState([])
  const [selectedTransaccionId, setSelectedTransaccionId] = useState(null)
  const [selectedTransaccion, setSelectedTransaccion] = useState(null)
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] = useState(null)
  const [rows, setRows] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [itemsMap, setItemsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [sortedData, setSortedData] = useState([])
  const [dniCliente, setDniCliente] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [filtersActive, setFiltersActive] = useState(false)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null)
  const [openModalDevolucion, setOpenModalDevolucion] = useState(false)
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState(null)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [deleteComentario, setDeleteComentario] = useState("")
  const [transaccionToDelete, setTransaccionToDelete] = useState(null)
  const [loadingMessage, setLoadingMessage] = useState("Cargando ventas...")

  // Gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false)
  const [loadingGraficas, setLoadingGraficas] = useState(false) // estado de carga para graficas
  const [tipoTransaccionFiltro, setTipoTransaccionFiltro] = useState("todos")
  const [datosOriginales, setDatosOriginales] = useState([])
  const [datosGraficas, setDatosGraficas] = useState([]) //datos para graficas

  // Estado para controlar el Drawer de filtros en responsive
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Estados para filtros de atributos de entidad
  const [opcionesPorAtributo, setOpcionesPorAtributo] = useState({})
  const [atributosEntidadFiltro, setAtributosEntidadFiltro] = useState({})

  // Estados para el menú lateral (solo desktop)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mostrarFiltroMontos, setMostrarFiltroMontos] = useState(false)
  const [mostrarFiltroCliente, setMostrarFiltroCliente] = useState(false)
  const [mostrarFiltroMediosPago, setMostrarFiltroMediosPago] = useState(false)

  // ===== Fechas =====
  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  // ===== Control del menú lateral =====
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  // Función para alternar filtros y cerrar otros
  const toggleFiltro = (filtroNombre) => {
    switch (filtroNombre) {
      case 'montos':
        setMostrarFiltroMontos(!mostrarFiltroMontos)
        setMostrarFiltroCliente(false)
        setMostrarFiltroMediosPago(false)
        break
      case 'cliente':
        setMostrarFiltroCliente(!mostrarFiltroCliente)
        setMostrarFiltroMontos(false)
        setMostrarFiltroMediosPago(false)
        break
      case 'mediosPago':
        setMostrarFiltroMediosPago(!mostrarFiltroMediosPago)
        setMostrarFiltroMontos(false)
        setMostrarFiltroCliente(false)
        break
      default:
        break
    }
  }

  // ===== Efectos iniciales =====
  useEffect(() => {
    const loadInitialData = async () => {
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
      setLoading(true)
      try {
        await fetchDataInicio()
        await fetchData(
          today.subtract(30, "day"),
      page,
          today,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      tipoTransaccionFiltro,
      selectedCanalEntidad
    )
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error)
      }
    }
    loadInitialData()
  }, [])

  /**
   * 🔄 REACTIVIDAD AUTOMÁTICA: Filtro "Desde"
   * 
   * Este useEffect escucha cambios en startDate (fecha "desde").
   * Cuando el usuario cambia la fecha en el DatePicker "Desde":
   * 1. setStartDate actualiza el estado
   * 2. Este useEffect detecta el cambio
   * 3. Llama automáticamente a fetchData con TODOS los filtros actuales
   * 4. La tabla se recarga mostrando resultados del nuevo rango de fechas
   * 
   * 💡 Ventaja: El usuario no necesita hacer clic en "Buscar" al cambiar fecha "desde"
   * 
   * 📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md > Sección "Interacción con Otros Filtros"
   */
  useEffect(() => {
    fetchData(
      startDate,              // 🎯 Observa cambios en esta variable
      page,                   // Mantiene página actual
      endDate,                // Mantiene fecha "hasta"
      dniCliente,             // Mantiene filtro de cliente
      selectedUbicacion,      // Mantiene filtro de ubicación
      minVentas,              // Mantiene monto mínimo
      maxVentas,              // Mantiene monto máximo
      mostrarDeletes,         // Mantiene estado de eliminados
      idTransaccionSearch,    // Mantiene búsqueda por ID
      tipoTransaccionFiltro,  // Mantiene tipo de transacción
      selectedCanalEntidad,   // Mantiene canal de entidad
    )
    // fetchData maneja setLoading(false) en su finally
  }, [startDate]) // ← Se ejecuta cada vez que startDate cambia

  /**
   * 📄 REACTIVIDAD AUTOMÁTICA: Paginación
   * 
   * Este useEffect escucha cambios en page (número de página).
   * Cuando el usuario cambia de página:
   * 1. setPage actualiza el estado
   * 2. Este useEffect detecta el cambio
   * 3. Llama automáticamente a fetchData con TODOS los filtros actuales
   * 4. La tabla muestra los resultados de la nueva página
   * 
   * 💡 Importante: Mantiene TODOS los filtros aplicados (fechas, cliente, ubicación, etc.)
   *    al cambiar de página, asegurando consistencia en la navegación.
   * 
   * 📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md > Sección "Paginación con Filtros Aplicados"
   */
  useEffect(() => {
    fetchData(
      startDate,              // Mantiene fecha "desde"
      page,                   // 🎯 Observa cambios en esta variable
      endDate,                // Mantiene fecha "hasta"
      dniCliente,             // Mantiene filtro de cliente
      selectedUbicacion,      // Mantiene filtro de ubicación
      minVentas,              // Mantiene monto mínimo
      maxVentas,              // Mantiene monto máximo
      mostrarDeletes,         // Mantiene estado de eliminados
      idTransaccionSearch,    // Mantiene búsqueda por ID
      tipoTransaccionFiltro,  // Mantiene tipo de transacción
      selectedCanalEntidad,   // Mantiene canal de entidad
    )
    // fetchData maneja setLoading(false) en su finally
  }, [page]) // ← Se ejecuta cada vez que page cambia

  // Efecto para cargar medios de pago cuando se selecciona un tipo
  useEffect(() => {
    if (selectedTipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  // ===== Filtros / datos =====
  
  /**
   * 🔍 BOTÓN "BUSCAR" - Filtrado Manual Combinado
   * 
   * Esta función se ejecuta cuando el usuario hace clic en el botón "Buscar".
   * Aplica TODOS los filtros seleccionados simultáneamente (operador AND lógico).
   * 
   * 🎯 Casos de uso:
   * - Usuario modifica varios filtros y quiere aplicarlos todos a la vez
   * - Usuario modifica fecha "hasta", cliente, ubicación, montos, etc.
   * - Usuario quiere refrescar resultados con filtros actuales
   * 
   * 💡 Diferencia con useEffect de startDate:
   * - useEffect: Se ejecuta automáticamente al cambiar fecha "desde"
   * - buttonFiltrado: Se ejecuta manualmente al hacer clic en "Buscar"
   * 
   * 🔄 Nota: Resetea a página 1 para mostrar primeros resultados del nuevo filtro
   * 
   * 📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md > Sección "Filtro Manual con Botón Buscar"
   */
  const buttonFiltrado = () => {
    fetchData(
      startDate,              // 📅 Fecha "desde" actual
      1,                      // 🔄 Reset a página 1 (primeros resultados)
      endDate,                // 📅 Fecha "hasta" actual
      dniCliente,             // 👤 Cliente seleccionado actual
      selectedUbicacion,      // 📍 Ubicación seleccionada actual
      minVentas,              // 💵 Monto mínimo actual
      maxVentas,              // 💵 Monto máximo actual
      mostrarDeletes,         // 🗑️ Estado actual de mostrar eliminados
      idTransaccionSearch,    // 🔍 ID de búsqueda actual
      tipoTransaccionFiltro,  // 📋 Tipo de transacción actual
      selectedCanalEntidad,   // 🏪 Canal de entidad actual
      100,                    // Límite de resultados (default)
      atributosEntidadFiltro, // 🏷️ Atributos personalizados actuales (ej: provincia, categoría)
    )
  }

  const fetchDataInicio = async () => {
    try {
      setLoading(true)
      await getUbicacion()
      await getTipoMedioDePago()
      await getEntidadAtributoNoEliminados(1) // Cargar atributos de entidad Cliente (ID 1)
      
      const canalEntidad = await getParametrosGlobales(["canalEntidad"])
      if (canalEntidad.canalEntidad == 1) {
        const datosCanalesEntidades = await getCanalEntidad()
        // console.log("datosCanalesEntidades:", datosCanalesEntidades)
        setCanalesEntidades(datosCanalesEntidades)
      }
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error)
    } finally {
      // No establecer loading en false aquí, fetchData lo hará
    }
  }

  // Cargar opciones para cada atributo cuando cambian los atributos disponibles
  const cargarOpcionesAtributos = async () => {
    if (!entidadAtributoNoEliminados || entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
      return
    }

    // console.log("Cargando opciones para atributos:", entidadAtributoNoEliminados)
    const opcionesMap = {}
    for (const attr of entidadAtributoNoEliminados) {
      try {
        const opciones = await getEntidadAtributoClasificacionByIdAtributo(attr.idEntidadAtributo || attr.id, 1) // 1 = Cliente
        opcionesMap[attr.idEntidadAtributo || attr.id] = opciones || []
        // console.log(`Opciones para atributo ${attr.idEntidadAtributo || attr.id} (${attr.descripcion}):`, opciones)
      } catch (error) {
        console.error(`Error al cargar opciones para atributo ${attr.id}:`, error)
        opcionesMap[attr.id] = []
      }
    }
    // console.log("Opciones por atributo cargadas:", opcionesMap)
    setOpcionesPorAtributo(opcionesMap)
  }

  // Inicializar opciones cuando se cargan los atributos disponibles
  useEffect(() => {
    if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0) {
      cargarOpcionesAtributos()
    } else if (entidadAtributoNoEliminados && entidadAtributoNoEliminados.length === 0) {
      setOpcionesPorAtributo({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entidadAtributoNoEliminados])

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value)
    if (newMin <= maxVentas) setMinVentas(newMin)
  }

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value)
    if (newMax >= minVentas) setMaxVentas(newMax)
  }

  /**
   * 🔍 FUNCIÓN PRINCIPAL DE CARGA DE DATOS CON FILTROS
   * 
   * Esta función implementa el patrón de referencia para filtros de fecha con timezone.
   * Todos los filtros funcionan simultáneamente mediante operador AND lógico.
   * 
   * 📖 Ver documentación completa en:
   * /documentacion/configuracion regional/FILTROS-FECHAS-Y-TIMEZONE.md
   * 
   * @param {dayjs.Dayjs} date - Fecha "desde" (startDate)
   * @param {number} page - Número de página actual
   * @param {dayjs.Dayjs} endDate - Fecha "hasta"
   * @param {Object|null} dniCliente - Cliente seleccionado { id, descripcion }
   * @param {Object|null} selectedUbicacion - Ubicación seleccionada { id, descripcion }
   * @param {number} valorMin - Monto mínimo de transacción
   * @param {number} valorMax - Monto máximo de transacción
   * @param {boolean} mostrarDeletes - Incluir transacciones eliminadas
   * @param {string|null} idTransaccionSearch - ID específico de transacción a buscar
   * @param {string|null} tipoTransaccionFiltro - Tipo de transacción filtrado
   * @param {Object|null} canalEntidad - Canal de entidad seleccionado
   * @param {number} limit - Límite de resultados por página (default: 100)
   * @param {Object} atributosEntidad - Atributos personalizados de entidad (ej: provincia, categoría)
   */
  const fetchData = async (
    date,
    page,
    endDate,
    dniCliente,
    selectedUbicacion,
    valorMin,
    valorMax,
    mostrarDeletes,
    idTransaccionSearch,
    tipoTransaccionFiltro,
    canalEntidad,
    limit = 100,
    atributosEntidad = {},
  ) => {
    const controller = new AbortController()
    const signal = controller.signal
    try {

      setLoading(true)
      setLoadingMessage("Cargando ventas...")

      // ========================================
      // 1️⃣ VALIDACIÓN Y FORMATO DE FECHAS
      // ========================================
      
      // Validar fecha "desde" (si es null, usar fecha actual)
      const validDate = date ? date : dayjs()
      let formattedDate = validDate.format("YYYY-MM-DD") // "2025-12-16"
      
      // Inicializar fecha "hasta" y otros IDs
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      // Validar y formatear fecha "hasta" (verificar que sea objeto dayjs)
      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD") // "2025-12-16"
      }
      
      // Extraer IDs de objetos de filtro (si existen)
      if (dniCliente != null) idDni = dniCliente.id
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id

      // ========================================
      // 2️⃣ NORMALIZACIÓN CON TIMEZONE (PASO CRÍTICO)
      // ========================================
      
      // 🌍 getFechaHoraActualClient agrega el timezone detectado del navegador
      // Convierte "2025-12-16" → "2025-12-16T00:00:00-03:00" (ISO 8601 con timezone)
      const result1 = await getFechaHoraActualClient(formattedDate)
      // .startOf("day") asegura que sea 00:00:00 (inicio del día)
      // .toDate() convierte de dayjs object a Date object nativo de JavaScript
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate()
      // Resultado: Date object que representa 2025-12-16 00:00:00 en zona horaria del cliente (ej: GMT-3)

      const result2 = await getFechaHoraActualClient(formattedDate2)
      // .endOf("day") asegura que sea 23:59:59 (fin del día)
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate()
      // Resultado: Date object que representa 2025-12-16 23:59:59 en zona horaria del cliente

      // console.log("canalEntidad ", canalEntidad)

      // ========================================
      // 3️⃣ PREPARACIÓN DE FILTROS ADICIONALES
      // ========================================
      
      // Extraer IDs de medios de pago seleccionados
      const tipoMedioDePagoId = selectedTipoMedioDePago ? selectedTipoMedioDePago.id : null
      const medioDePagoId = selectedMedioDePago ? selectedMedioDePago.id : null

      // ========================================
      // 4️⃣ LLAMADA AL BACKEND CON TODOS LOS FILTROS
      // ========================================
      
      // 🔗 Todos los filtros se envían simultáneamente (operador AND lógico en el backend)
      const data = await getTransaccionTransformedVentas(
        1,                          // idTipoEntidad: 1 = Cliente
        formattedDate,              // 📅 Fecha "desde" (Date object con timezone)
        page,                       // Página actual de la paginación
        limit,                      // Límite de resultados por página
        { signal },                 // Señal para abortar request si es necesario
        formattedDate2,             // 📅 Fecha "hasta" (Date object con timezone)
        idDni,                      // 👤 Filtro de cliente (ID)
        idSelectedUbicacion,        // 📍 Filtro de ubicación (ID)
        valorMin,                   // 💵 Monto mínimo
        valorMax,                   // 💵 Monto máximo
        mostrarDeletes,             // 🗑️ Incluir transacciones eliminadas
        idTransaccionSearch,        // 🔍 Buscar por ID específico de transacción
        "traerDevolucion",          // Incluir transacciones de tipo devolución
        "",                         // Buscar por CAE (vacío = no filtrar)
        tipoTransaccionFiltro,      // 📋 Tipo de transacción (Venta, Presupuesto, etc.)
        canalEntidad?.id || null,   // 🏪 Canal de entidad (Online, Presencial, etc.)
        false,                      // soloFacturadas: false = traer todas
        tipoMedioDePagoId,          // 💳 Tipo de medio de pago (Efectivo, Tarjeta, etc.)
        medioDePagoId,              // 💳 Medio de pago específico (Visa, Mastercard, etc.)
        atributosEntidad,           // 🏷️ Atributos personalizados de entidad (ej: { provincia: "Buenos Aires" })
      )
      // console.log("data para saber que trae el backend", data)

      setDatosOriginales(data.data)

      const transaccionTransformedReforged = data.data.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }))

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion))

        return {
          id: transaccion.id,
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          apellido: transaccion.entidad?.apellido || "",
          direccion: transaccion.entidad?.direccion || "",
          localidad: transaccion.entidad?.localidad || "",
          provincia: transaccion.entidad?.provincia || "",
          usuario: transaccion.usuario?.usuario || "",
          negocio: transaccion.negocio?.descripcion || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          idUbicacion: transaccion.idUbicacion || null, // ✅ ID de ubicación para recalcular caja
          idEntidadNumero: transaccion.idEntidad,
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          impuestos: transaccion.impuestos,
          montoDescuento: transaccion.montoDescuento,
          tipoFacturaDescripcion: transaccion.tipoFacturaDescripcion,
          porcentajeDescuento: transaccion.porcentajeDescuento,
          tipoTransaccion: transaccion.tipoTransaccion,
          idTipoTransaccionID: transaccion.idTipoTransaccion,
          transaccionAsociada: transaccion.transaccionAsociada || null,
          canal: transaccion.entidad?.canalEntidad?.descripcion || "",
          usuarioElimino: transaccion.usuarioElimino || null,
          eliminado: transaccion.eliminado || false, // 🔒 Propiedad para identificar transacciones eliminadas
        }
      })

      // console.log("transaccionTransformedReforged", transaccionTransformedReforged)

      setPage(data.currentPage)
      setTotalPages(data.totalPages)
      setTotalCount(data.total)
      setMontoTotalTransacciones(data.totalMontoVentas)
      setSortedData(transaccionTransformedReforged)
    } catch (error) {
      console.error("Error al cargar los datos:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value)

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1)
      // No afecta el loading principal de la tabla
    }
  }, [searchTextCliente])

  useEffect(() => {
    const fetchItemsDetails = async () => {
      const itemsDetails = {}
      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem)
          if (foundItem) itemsDetails[transaccionItem.idItem] = foundItem
        }),
      )
      setItemsMap(itemsDetails)
    }
    if (transaccionItems.length > 0) {
    fetchItemsDetails()
      // No afecta el loading principal de la tabla
    }
  }, [transaccionItems, getItemById])

  // Columnas generadas dinámicamente usando useMemo
  const columns = useMemo(() => {
    // Columnas esenciales para móvil/tablet
    const essentialColumns = [
      {
        field: "id",
        headerName: "ID",
        flex: isLaptopDown ? 0.4 : 0.3,
        minWidth: isLaptopDown ? 60 : 70,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: isLaptopDown ? "0.75rem" : "0.875rem" }}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Fecha",
        flex: isLaptopDown ? 1.2 : 1,
        minWidth: isLaptopDown ? 120 : 200,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 0.5, alignItems: "center" }}>
            <CalendarTodayIcon fontSize={isLaptopDown ? "small" : "small"} sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" sx={{ fontSize: isLaptopDown ? "0.7rem" : "0.875rem" }}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "montoTotal",
        headerName: "Monto",
        type: "number",
        flex: isLaptopDown ? 1 : 0.8,
        minWidth: isLaptopDown ? 100 : 130,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => {
          const esDevolucion = params.row?.idTipoTransaccionID === 6
          return (
            <Chip
              icon={<AttachMoneyIcon fontSize={isLaptopDown ? "small" : "small"} />}
              label={params.value?.toLocaleString("es-AR") || "0.00"}
              size={isLaptopDown ? "small" : "small"}
              sx={{
                fontWeight: "bold",
                bgcolor: esDevolucion ? alpha("#9e9e9e", 0.1) : alpha("#4CAF50", 0.1),
                color: esDevolucion ? "#757575" : "#4CAF50",
                "& .MuiChip-icon": { color: esDevolucion ? "#757575" : "#4CAF50" },
                display: "flex",
                fontSize: isLaptopDown ? "0.7rem" : "0.875rem",
                height: isLaptopDown ? 24 : 28,
              }}
            />
          )
        },
      },
      {
        field: "ubicacion",
        headerName: "Ubicación",
        flex: isLaptopDown ? 1 : 1,
        minWidth: isLaptopDown ? 100 : 150,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOnIcon fontSize={isLaptopDown ? "small" : "small"} sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" sx={{ fontSize: isLaptopDown ? "0.75rem" : "0.875rem" }}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "action",
        headerName: "Acciones",
        flex: isLaptopDown ? 1.2 : 0.8,
        minWidth: isLaptopDown ? 120 : 150,
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          const esDevolucion = params.row?.idTipoTransaccionID === 6
          return (
            <Box sx={{ display: "flex", gap: 0.25 }}>
              <Tooltip title="Ver detalles">
                <IconButton
                  className="colorGeneralParaIconos"
                  onClick={() => handleOpenModal(params.row)}
                  size={isLaptopDown ? "small" : "small"}
                  sx={{ 
                    bgcolor: alpha("#5b5b5f", 0.1), 
                    "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                    padding: isLaptopDown ? "4px" : "8px",
                  }}
                >
                  <VisibilityIcon fontSize={isLaptopDown ? "small" : "small"} />
                </IconButton>
              </Tooltip>

              {!esDevolucion && (
                <>
                  <Tooltip title="Descargar PDF">
                    <IconButton
                      className="colorGeneralParaIconos"
                      onClick={() => handleDownloadPDF(params.row)}
                      size={isLaptopDown ? "small" : "small"}
                      sx={{ 
                        bgcolor: alpha("rgb(244, 177, 14)", 0.1), 
                        "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
                        padding: isLaptopDown ? "4px" : "8px",
                      }}
                    >
                      <PictureAsPdfIcon fontSize={isLaptopDown ? "small" : "small"} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Descargar remito">
                    <IconButton
                      className="colorGeneralParaIconos"
                      onClick={() => handleDownloadRemito(params.row)}
                      size={isLaptopDown ? "small" : "small"}
                      sx={{ 
                        bgcolor: alpha("#4CAF50", 0.1), 
                        "&:hover": { bgcolor: alpha("#4CAF50", 0.2) },
                        padding: isLaptopDown ? "4px" : "8px",
                      }}
                    >
                      <ReceiptLongIcon fontSize={isLaptopDown ? "small" : "small"} />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {/* Botón Eliminar Venta - Solo visible si tiene permiso granular, la transacción NO está eliminada Y NO se está mostrando la vista de eliminados */}
              {tieneAccesoFuncionalidad(user, 'VENTAS2', 'btnEliminarTransaccion') && 
               !params.row.eliminado && 
               !mostrarDeletes && (
                <Tooltip title="Borrar Transaccion">
                  <IconButton
                    className="colorGeneralParaIconos"
                    onClick={() => borrarTransaccion(params.row)}
                    size={isLaptopDown ? "small" : "small"}
                    sx={{ 
                      bgcolor: alpha("rgb(244, 177, 14)", 0.1), 
                      "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
                      padding: isLaptopDown ? "4px" : "8px",
                    }}
                  >
                    <DeleteIcon fontSize={isLaptopDown ? "small" : "small"} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        },
      },
    ]

    // Si es móvil/tablet, retornar solo columnas esenciales
    if (isLaptopDown) {
      return essentialColumns
    }

    // Columnas completas para desktop
    const baseColumns = [
      ...essentialColumns.slice(0, 3), // ID, Fecha, Monto
      {
        field: "descripcionEntidad",
        headerName: "Cliente",
        flex: 1.2,
        headerAlign: "center",
        minWidth: 150,
        sortable: true,
        valueGetter: (value, row) => {
          if (!row) return "";
          return `${row.descripcionEntidad || ""} ${row.apellido || ""}`.trim();
        },
        renderCell: (params) => (
          <Tooltip title={`${params.row.descripcionEntidad} ${params.row.apellido || ""}`}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={"500"}>
                {params.row.descripcionEntidad} {params.row.apellido || ""}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "idTipoTransaccion",
        headerName: "Tipo",
        flex: 0.8,
        minWidth: 120,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => {
          const esDevolucion = params.row?.idTipoTransaccionID === 6
          return (
            <Chip
              label={params.value}
              size="small"
              sx={{
                fontWeight: "bold",
                bgcolor: esDevolucion ? alpha("#9e9e9e", 0.1) : alpha("#4CAF50", 0.1),
                color: esDevolucion ? "#757575" : "#4CAF50",
                display: "flex",
              }}
            />
          )
        },
      },
      {
        field: "pagos",
        headerName: "Medio de Pago",
        flex: 1,
        minWidth: 200,
        headerAlign: "center",
        sortable: true,
        valueGetter: (value, row) => {
          if (!row) return "N/A";
          const pagos = Array.isArray(row.pagos) ? row.pagos : [];
          if (pagos.length > 1) return "Múltiple";
          return pagos[0]?.descripcion || "N/A";
        },
        renderCell: (params) => {
          const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : []
          if (pagos.length > 1) {
            return <Chip label="Múltiple" size="small" color="info" sx={{ fontWeight: "bold", display: "flex" }} />
          }
          return (
            <Chip
              icon={<PointOfSaleIcon fontSize="small" />}
              label={pagos[0]?.descripcion || "N/A"}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                color: "#00AAE4",
                borderColor: alpha("#00AAE4", 0.5),
                "& .MuiChip-icon": { color: "#00AAE4" },
                display: "flex",
              }}
            />
          )
        },
      },
      {
        field: "usuario",
        headerName: "Vendedor",
        flex: 0.8,
        minWidth: 150,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => <Typography variant="body2" sx={{ fontStyle: "italic" }}>{params.value}</Typography>,
      },
      // Columna de usuario que eliminó (solo visible cuando se muestran eliminados)
      ...(mostrarDeletes ? [{
        field: "usuarioElimino",
        headerName: "Eliminado por",
        flex: 0.8,
        minWidth: 150,
        headerAlign: "center",
        sortable: true,
        renderCell: (params) => {
          if (params.value) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DeleteIcon fontSize="small" sx={{ color: alpha("#dc3545", 0.7) }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontStyle: "italic",
                    color: "#dc3545",
                    fontWeight: 500,
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            )
          }
          return (
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              N/A
            </Typography>
          )
        },
      }] : []),
      essentialColumns[3], // Ubicación
      {
        headerName: "Venta asociada",
        field: "transaccionAsociada",
        flex: 1,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => {
          if (params.value !== null && params.value !== undefined) {
            return (
              <Button variant="contained" size="small" onClick={() => handleVerTransaccionAsociada(params.value)}>
                {params.value}
              </Button>
            )
          } else {
            return <Typography variant="body2">No existe</Typography>
          }
        },
      },
      essentialColumns[4], // Acciones
    ]

    // Agregar columna de canal si está habilitado (solo en desktop)
    if (canalesEntidades && canalesEntidades.length > 0) {
      baseColumns.splice(-1, 0, {
        field: "canal",
        headerName: "Canal",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ChannelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value || "Sin canal"}</Typography>
          </Box>
        ),
      })
    }

    return baseColumns
  }, [canalesEntidades, isLaptopDown, tieneAccesoFuncionalidad, user, mostrarDeletes])

  const totalVentas = useMemo(() => rows.reduce((acc, row) => acc + (row.montoTotal || 0), 0), [rows])

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
        }}
      >
        Total Ventas: <span style={{ color: "#4CAF50" }}>${totalVentas.toLocaleString("es-AR")}</span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  )

  const handleVerTransaccionAsociada = async (id) => {
    await fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      id,
      tipoTransaccionFiltro,
      selectedCanalEntidad,
    )
  }

  const borrarTransaccion = (transaccionRow) => {
    setTransaccionToDelete(transaccionRow)
    setDeleteComentario("")
    setOpenDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!transaccionToDelete) return

    // Validar que el comentario no esté vacío
    if (!deleteComentario.trim()) {
      Swal.fire({
        title: "Campo requerido",
        text: "Debes proporcionar un motivo para eliminar la transacción.",
        icon: "warning",
        confirmButtonColor: "#f4b10e",
      });
      return;
    }

    // Cerrar modal de eliminación antes de mostrar loading
    setOpenDeleteModal(false)

    try {
      setLoading(true)
      
      // ✅ Verificar si la transacción afecta cuenta corriente
      let afectaCuentaCorriente = false
      let entidadNombre = null
      
      try {
        setLoadingMessage("(1/4) Verificando cuenta corriente...")
        
        // Verificar si tiene flag de cuenta corriente
        if (transaccionToDelete.afectaCuentaCorriente) {
          afectaCuentaCorriente = true
        }
        
        // O si tiene pagos que afectan cuenta corriente
        if (transaccionToDelete.pagos && Array.isArray(transaccionToDelete.pagos)) {
          const tieneCC = transaccionToDelete.pagos.some(pago => 
            pago.medioDePago?.tipoMedioDePago?.afectaCuentaCorriente === true
          )
          if (tieneCC) {
            afectaCuentaCorriente = true
          }
        }
        
        // Obtener nombre de la entidad
        if (afectaCuentaCorriente && transaccionToDelete.idEntidad) {
          try {
            const entidad = await getEntidadByID(transaccionToDelete.idEntidad)
            if (entidad) {
              entidadNombre = `${entidad.descripcion || ''} ${entidad.apellido || ''}`.trim()
            }
          } catch (err) {
            console.log("No se pudo obtener entidad:", err)
          }
        }
      } catch (ccError) {
        console.log("Error verificando CC:", ccError)
      }
      
      // ✅ PASO 2: Eliminar transacción
      setLoadingMessage("(2/4) Eliminando transacción...")
      
      // Guardar datos de la transacción ANTES de eliminar (para recalcular caja)
      const fechaTransaccion = transaccionToDelete.fechaHoraCreacion
      const idUbicacionTransaccion = transaccionToDelete.idUbicacion
      const zona = Intl.DateTimeFormat().resolvedOptions().timeZone
      
      // Guardar auditoría antes de eliminar
      if (user && user.id) {
        await postTransaccionAuditoria(
          transaccionToDelete.id,
          user.id,
          deleteComentario.trim(),
          "eliminar"
        );
      }

      // Eliminar la transacción
      await updateDeleteTransaccion(transaccionToDelete.id);
      
      // ✅ PASO 3: Actualizar cuenta corriente (si aplica)
      if (afectaCuentaCorriente) {
        setLoadingMessage("(3/4) Actualizando cuenta corriente...")
        // El backend ya se encargó de actualizar la CC en updateDeleteTransaccion
        // Solo esperamos un momento para dar feedback visual
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // ✅ PASO 4: Recalcular caja
      setLoadingMessage(`(${afectaCuentaCorriente ? '4/4' : '3/4'}) Recalculando caja...`)
      
      try {
        // Formatear fecha para la API (YYYY-MM-DD)
        const fechaFormateada = dayjs(fechaTransaccion).format('YYYY-MM-DD')
        
        // Determinar ubicación para recalcular
        // Prioridad: ubicación filtrada > ubicación de transacción > todas
        const ubicacionParaRecalculo = selectedUbicacion?.id || idUbicacionTransaccion || 'todas'
        
        // Recalcular caja desde esa fecha hasta hoy
        await getAndPostCajaDatos(
          fechaFormateada,        // Fecha de la transacción eliminada
          zona,                   // Zona horaria
          ubicacionParaRecalculo, // Ubicación específica o 'todas'
          "update"                // ⭐ Modo UPDATE para recalcular desde esa fecha
        )
        
        // console.log(`✅ Caja recalculada desde ${fechaFormateada} para ubicación ${ubicacionParaRecalculo}`)
      } catch (cajaError) {
        console.error("⚠️ Error al recalcular caja:", cajaError)
        // No bloqueamos el flujo si falla el recalculo de caja
        // La transacción ya fue eliminada exitosamente
      }
      
      // Limpiar estados
      setTransaccionToDelete(null)
      setDeleteComentario("")
      
      // Refrescar datos
      setLoadingMessage("Actualizando tabla...")
      await fetchData(
        startDate,
        page,
        endDate,
        dniCliente,
        selectedUbicacion,
        minVentas,
        maxVentas,
        mostrarDeletes,
        idTransaccionSearch,
        tipoTransaccionFiltro,
        selectedCanalEntidad,
      );
      
      setLoading(false)
      
      // ✅ Mensaje personalizado según si afectó cuenta corriente
      let mensajeExito = `La transacción ${transaccionToDelete.id} ha sido eliminada correctamente.`
      
      if (afectaCuentaCorriente) {
        mensajeExito += `\n\n💳 La cuenta corriente ${entidadNombre ? 'de ' + entidadNombre : ''} ha sido actualizada automáticamente.`
      }
      
      Swal.fire({
        title: "¡Eliminado!",
        text: mensajeExito,
        icon: "success",
        confirmButtonColor: "#4caf50",
        timer: afectaCuentaCorriente ? 4000 : 2000,
        showConfirmButton: afectaCuentaCorriente,
      });
    } catch (error) {
      console.error("Error al borrar transacción:", error);
      setLoading(false)
      setTransaccionToDelete(null)
      setDeleteComentario("")
      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar la transacción. Intenta de nuevo.",
        icon: "error",
      });
    }
  }

  const handleCancelDelete = () => {
    setOpenDeleteModal(false)
    setTransaccionToDelete(null)
    setDeleteComentario("")
  }

  const handleDownloadPDF = async (transaccionRow) => {
    try {
      setLoading(true)
      // ✅ Pasar mostrarDeletes para traer items eliminados si corresponde
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id, mostrarDeletes)
      // console.log("transaccionEncontrada: ", transaccionEncontrada)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const precio = transItem.precio || 0
          return {
            idItem: transItem.idItem,
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            porcentajeInteres: transItem.porcentajeInteres || 0,
            total: (
              precio *
              (transItem.cantidad || 0) *
              (1 - (transItem.porcentajeDescuento || 0) / 100) *
              (1 + (transItem.porcentajeInteres || 0) / 100)
            ).toFixed(2),
          }
        }),
      )
      generarFacturaPDF(transaccionRow, itemsTransaccion, entidadCliente, "tablaVenta", transaccionRow.impuestos)
    } catch (error) {
      console.error("Error al generar PDF:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleMostrarDeletesChange = (event) => setMostrarDeletes(event.target.checked)

  const handleDownloadRemito = async (transaccionRow) => {
    try {
      setLoading(true)
      // ✅ Pasar mostrarDeletes para traer items eliminados si corresponde
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id, mostrarDeletes)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      
      // ✅ Obtener cuenta corriente del cliente para mostrar en el remito
      let cuentaCorrienteInfo = null
      try {
        const cuentas = await getCuentaCorrienteByIdEntidad(entidadCliente?.id)
        const cuenta = cuentas && cuentas.length > 0 ? cuentas.find((ct) => ct.idEntidad === entidadCliente?.id) : null
        if (cuenta) {
          cuentaCorrienteInfo = {
            saldo: cuenta.saldo,
            tieneCuentaCorriente: true
          }
        } else {
          cuentaCorrienteInfo = {
            saldo: 0,
            tieneCuentaCorriente: false
          }
        }
      } catch (error) {
        // console.error("Error al obtener cuenta corriente:", error)
        cuentaCorrienteInfo = {
          saldo: 0,
          tieneCuentaCorriente: false
        }
      }
      
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const precio = transItem.precio || 0
          return {
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            porcentajeInteres: transItem.porcentajeInteres || 0,
            total: (
              precio *
              (transItem.cantidad || 0) *
              (1 - (transItem.porcentajeDescuento || 0) / 100) *
              (1 + (transItem.porcentajeInteres || 0) / 100)
            ).toFixed(2),
          }
        }),
      )
      const tipoFacturaDescripcion = transaccionRow.tipoFacturaDescripcion != "" ? false : true
      generarRemitoPDF(
        transaccionRow,
        itemsTransaccion,
        entidadCliente,
        transaccionRow.usuario,
        tipoFacturaDescripcion,
        transaccionRow.impuestos,
        cuentaCorrienteInfo,
      )
    } catch (error) {
      console.error("Error al generar remito:", error)
    } finally {
      setLoading(false)
    }
  }

  const [calculatedTotal, setCalculatedTotal] = useState(null)
  const [totalMontoDescuento, setTotalMontoDescuento] = useState(null)
  const [totalPorcentajeDescuento, setPorcentajeDescuento] = useState(null)
  const [montoSinImpuesto, setMontoSinImpuesto] = useState(null)

  const handleOpenModal = async (transaccionRow) => {
    try {
      setLoading(true)
      setSelectedTransaccion(transaccionRow)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      setSelectedTransaccionEntidad(entidadCliente)

      // ✅ Pasar mostrarDeletes para traer items eliminados si corresponde
      const items = await getTransaccionItemID(transaccionRow.id, mostrarDeletes)
      setTransaccionItems(items)

      const { montoTotal, impuestos = [], montoDescuento = 0, porcentajeDescuento = 0 } = transaccionRow
      const totalSinImpuestos = impuestos.reduce((total, impuesto) => total - (impuesto.montoTotal || 0), montoTotal)
      const totalSinImpuestosYDescuento = totalSinImpuestos + montoDescuento
      const totalFinal = totalSinImpuestosYDescuento * (1 - porcentajeDescuento / 100)

      setCalculatedTotal(totalFinal)
      setTotalMontoDescuento(montoDescuento)
      setPorcentajeDescuento(porcentajeDescuento)
      setMontoSinImpuesto(totalSinImpuestos)

      switch (transaccionRow.idTipoTransaccionID) {
        case 1: // venta
          setOpenModal(true)
          break
        case 6: // devolucion
          setOpenModalDevolucion(true)
          break
      }
    } catch (error) {
      console.error("Error al abrir modal:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setOpenModalDevolucion(false)
  }

  const handleOpenGraficasModal = async () => {
    setLoadingGraficas(true) // estado de carga para graficas
    setOpenGraficasModal(true) // abrir modal de graficas
    
    try {
      const validDate = startDate ? startDate : dayjs() // fecha de inicio
      let formattedDate = validDate.format("YYYY-MM-DD") // fecha de inicio formateada
      let formattedDate2 = endDate // fecha de fin
      let idDni = null // id del cliente
      let idSelectedUbicacion = null // id de la ubicación

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD") // fecha de fin formateada
      }
      if (dniCliente != null) idDni = dniCliente.id // id del cliente
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id // id de la ubicación

      const result1 = await getFechaHoraActualClient(formattedDate) // pedimos la fecha del servidor
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate() // formateamos la fecha

      const result2 = await getFechaHoraActualClient(formattedDate2) // pedimos la fecha del servidor
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate() // formateamos la fecha

      // Llamar a la nueva función SIN PAGINADO para obtener todos los datos
      const allData = await getTransaccionTransformedVentasAllData(
        1, // tipoTransaccion: 1 = venta
        formattedDate, // fecha de inicio
        formattedDate2, // fecha de fin
        idDni, // id del cliente
        idSelectedUbicacion, // id de la ubicación
        minVentas, // monto mínimo
        maxVentas, // monto máximo
        mostrarDeletes, // mostrar eliminados
        idTransaccionSearch, // id de la transacción
        "traerDevolucion", // traer devoluciones
        "", // Buscar por CAE
        tipoTransaccionFiltro, // tipo de transacción
        selectedCanalEntidad?.id || null, // canal de entidad
        atributosEntidadFiltro // filtros de atributos de entidad
      )

      // console.log("Datos completos para gráficas:", allData)
      setDatosGraficas(allData.data)
    } catch (error) {
      console.error("Error al cargar datos para gráficas:", error)
      setDatosGraficas([])
    } finally {
      setLoadingGraficas(false)
    }
  }

  // Función para generar PDF de ventas
  const generarPDFVentas = async () => {
    try {
      setLoading(true)

      // Validar que hay datos para generar el PDF
      if (!sortedData || sortedData.length === 0) {
        Swal.fire({
          title: "Sin datos",
          text: "No hay transacciones para generar el PDF",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
        })
        setLoading(false)
        return
      }

      // Obtener todos los datos sin paginación (similar a gráficas)
      const validDate = startDate ? startDate : dayjs()
      let formattedDate = validDate.format("YYYY-MM-DD")
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }
      if (dniCliente != null) idDni = dniCliente.id
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id

      const result1 = await getFechaHoraActualClient(formattedDate)
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate()

      const result2 = await getFechaHoraActualClient(formattedDate2)
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate()

      // Obtener todos los datos filtrados
      const allData = await getTransaccionTransformedVentasAllData(
        1, // tipoTransaccion: 1 = venta
        formattedDate,
        formattedDate2,
        idDni || "",
        idSelectedUbicacion || "",
        minVentas || "",
        maxVentas || "",
        mostrarDeletes,
        idTransaccionSearch || "",
        "traerDevolucion",
        "", // Buscar por CAE
        tipoTransaccionFiltro || "",
        selectedCanalEntidad?.id || "",
      )

      // Transformar datos para el PDF (similar a fetchData)
      const transaccionesParaPDF = allData.data.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }))

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion))

        return {
          id: transaccion.id,
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          apellido: transaccion.entidad?.apellido || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          usuario: transaccion.usuario?.usuario || "",
          canal: transaccion.entidad?.canalEntidad?.descripcion || "",
          transaccionAsociada: transaccion.transaccionAsociada || null,
          idTipoTransaccionID: transaccion.idTipoTransaccion,
        }
      })

      // Calcular totales
      const totalVentas = transaccionesParaPDF.reduce((acc, row) => acc + (row.montoTotal || 0), 0)

      // Crear PDF en formato apaisado (landscape)
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      let yPosition = 20

      // Colores
      const darkNavy = [26, 26, 26]
      const lightGray = [240, 240, 240]

      // ===========================================
      // ENCABEZADO
      // ===========================================
      doc.setFontSize(18)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Reporte de Ventas", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 10

      // Fechas del reporte
      doc.setFontSize(10)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(100, 100, 100)
      const fechaInicio = startDate.format("DD/MM/YYYY")
      const fechaFin = endDate.format("DD/MM/YYYY")
      doc.text(`Período: ${fechaInicio} - ${fechaFin}`, pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      // Filtros aplicados
      doc.setFontSize(9)
      doc.setFont("Helvetica", "italic")
      const filtros = []
      if (selectedUbicacion) filtros.push(`Ubicación: ${selectedUbicacion.descripcion || selectedUbicacion.label}`)
      if (dniCliente) filtros.push(`Cliente: ${dniCliente.descripcion || dniCliente.label || ""}`)
      if (tipoTransaccionFiltro !== "todos") {
        const tipoLabel = tipoTransaccionFiltro === "ventas" ? "Solo Ventas" : "Solo Devoluciones"
        filtros.push(`Tipo: ${tipoLabel}`)
      }
      if (selectedCanalEntidad) filtros.push(`Canal: ${selectedCanalEntidad.descripcion || selectedCanalEntidad.label || ""}`)
      if (selectedTipoMedioDePago) filtros.push(`Tipo Pago: ${selectedTipoMedioDePago.descripcion}`)
      if (selectedMedioDePago) filtros.push(`Medio Pago: ${selectedMedioDePago.descripcion}`)
      if (minVentas > 0) filtros.push(`Mínimo: $${minVentas.toLocaleString("es-AR")}`)
      if (maxVentas > 0) filtros.push(`Máximo: $${maxVentas.toLocaleString("es-AR")}`)
      if (idTransaccionSearch) filtros.push(`ID: ${idTransaccionSearch}`)
      if (mostrarDeletes) filtros.push("Incluye eliminados")

      if (filtros.length > 0) {
        doc.text(`Filtros: ${filtros.join(" | ")}`, pageWidth / 2, yPosition, { align: "center", maxWidth: pageWidth - 30 })
        yPosition += 6
      } else {
        doc.text("Filtros: Todos", pageWidth / 2, yPosition, { align: "center" })
        yPosition += 6
      }

      yPosition += 5

      // ===========================================
      // RESUMEN
      // ===========================================
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Resumen", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      doc.setFontSize(9)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(60, 60, 60)

      const resumenWidth = 100
      const resumenX = (pageWidth - resumenWidth) / 2
      let resumenY = yPosition
      const resumenTextX = resumenX + 8

      // Total de transacciones
      doc.setFont("Helvetica", "bold")
      doc.text("Total de transacciones:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      doc.text(transaccionesParaPDF.length.toString(), resumenX + resumenWidth - 8, resumenY, { align: "right" })
      resumenY += 7

      // Total ventas
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(76, 175, 80) // Verde
      doc.text("Total Ventas:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalVentas),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )

      yPosition = resumenY + 12

      // ===========================================
      // TABLA DE TRANSACCIONES
      // ===========================================
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Transacciones", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      // Preparar datos para la tabla
      const tableData = transaccionesParaPDF.map((transaccion) => {
        const medioPago = transaccion.pagos && transaccion.pagos.length > 0
          ? transaccion.pagos.length > 1
            ? "Múltiple"
            : transaccion.pagos[0].descripcion
          : "N/A"

        const cliente = `${transaccion.descripcionEntidad || ""} ${transaccion.apellido || ""}`.trim() || "Sin cliente"

        const tipoTransaccion = transaccion.idTipoTransaccionID === 6 ? "Devolución" : transaccion.idTipoTransaccion

        return [
          transaccion.id || "",
          transaccion.createdAt || "",
          cliente,
          tipoTransaccion,
          new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: "ARS",
            minimumFractionDigits: 2,
          }).format(transaccion.montoTotal),
          medioPago,
          transaccion.usuario || "",
          transaccion.ubicacion || "",
          transaccion.transaccionAsociada ? transaccion.transaccionAsociada.toString() : "-",
          canalesEntidades && canalesEntidades.length > 0 ? (transaccion.canal || "Sin canal") : null,
        ].filter((item) => item !== null) // Filtrar columna de canal si no está habilitada
      })

      // Agregar fila de totales
      const totalRow = [
        "TOTALES",
        "",
        "",
        "",
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalVentas),
        "",
        "",
        "",
        "",
        canalesEntidades && canalesEntidades.length > 0 ? "" : null,
      ].filter((item) => item !== null)

      tableData.push(totalRow)

      // Definir encabezados según si hay canal o no
      const headers = canalesEntidades && canalesEntidades.length > 0
        ? [["ID", "Fecha", "Cliente", "Tipo", "Monto", "Medio de Pago", "Vendedor", "Ubicación", "Venta Asociada", "Canal"]]
        : [["ID", "Fecha", "Cliente", "Tipo", "Monto", "Medio de Pago", "Vendedor", "Ubicación", "Venta Asociada"]]

      // Generar tabla con autoTable
      doc.autoTable({
        startY: yPosition,
        head: headers,
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: darkNavy,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [60, 60, 60],
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles: {
          0: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // ID
          1: { cellWidth: "auto", halign: "center", minCellWidth: 35 }, // Fecha
          2: { cellWidth: "auto", halign: "left", minCellWidth: 40 }, // Cliente
          3: { cellWidth: "auto", halign: "center", minCellWidth: 30 }, // Tipo
          4: { cellWidth: "auto", halign: "right", minCellWidth: 30 }, // Monto
          5: { cellWidth: "auto", halign: "center", minCellWidth: 30 }, // Medio de Pago
          6: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Vendedor
          7: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Ubicación
          8: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Venta Asociada
          ...(canalesEntidades && canalesEntidades.length > 0 && {
            9: { cellWidth: "auto", halign: "center", minCellWidth: 25 }, // Canal
          }),
        },
        styles: {
          cellPadding: 2,
          overflow: "linebreak",
          halign: "left",
        },
        didParseCell: function (data) {
          // Estilo para la fila de totales
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fillColor = [219, 234, 254] // Azul claro
            data.cell.styles.fontStyle = "bold"
            data.cell.styles.fontSize = 8
          }
          // Estilo para devoluciones
          if (data.row.raw && data.row.raw.idTipoTransaccionID === 6) {
            data.cell.styles.textColor = [117, 117, 117] // Gris
          }
        },
        margin: { left: 0, right: 0, top: yPosition, bottom: 25 },
        // Repetir encabezados en cada página
        showHead: "everyPage",
        // Ajustar el margen superior en páginas siguientes para aprovechar toda la altura
        didDrawPage: function (data) {
          // A partir de la primera página (donde está la tabla), ajustar el margen superior
          // En la primera página, ya tenemos yPosition, pero en páginas siguientes empezar desde arriba
          if (data.pageNumber > 1) {
            // En páginas siguientes, empezar desde muy arriba (solo dejar espacio mínimo)
            data.settings.margin.top = 5
          }
        },
      })

      // Obtener la posición Y final después de la tabla
      const finalY = doc.lastAutoTable.finalY || yPosition + 50

      // ===========================================
      // PIE DE PÁGINA
      // ===========================================
      const pieY = pageHeight - 20
      doc.setFontSize(8)
      doc.setFont("Helvetica", "normal")
      doc.setTextColor(120, 120, 120)

      const fechaHoraGeneracion = new Date().toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })

      doc.text(
        `Generado el ${fechaHoraGeneracion} por ${user?.usuario || "Usuario"}`,
        pageWidth / 2,
        pieY,
        { align: "center" }
      )

      // Número de página
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        )
      }

      // Descargar PDF
      const nombreArchivo = `Reporte_Ventas_${fechaInicio.replace(/\//g, "-")}_${fechaFin.replace(/\//g, "-")}.pdf`
      doc.save(nombreArchivo)

      Swal.fire({
        title: "PDF Generado",
        text: "El reporte se ha descargado correctamente",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (error) {
      console.error("Error al generar PDF:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudo generar el PDF. Intente nuevamente.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCloseGraficasModal = () => setOpenGraficasModal(false)

  /**
   * 🧹 RESETEAR TODOS LOS FILTROS
   * 
   * Esta función limpia TODOS los filtros y restaura valores por defecto.
   * Se ejecuta cuando el usuario hace clic en "Resetear Filtros".
   * 
   * 🔄 Valores restaurados:
   * - Fechas: Últimos 30 días (default del sistema)
   * - Cliente: null (sin filtro)
   * - Ubicación: null (sin filtro)
   * - Montos: 0 a 0 (sin filtro)
   * - Mostrar eliminados: false
   * - ID búsqueda: "" (sin filtro)
   * - Tipo transacción: "todos"
   * - Canal entidad: null
   * - Medios de pago: null
   * - Atributos personalizados: {} (vacío)
   * 
   * 💡 Después de resetear, recarga los datos con solo el rango de fechas default
   * 
   * 📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md > Sección "Funciones de Utilidad"
   */
  const resetFilters = () => {
    // Limpiar filtros de entidades
    setDniCliente(null)              // 👤 Sin filtro de cliente
    setSelectedUbicacion(null)       // 📍 Sin filtro de ubicación
    setSearchTextCliente("")         // Limpiar búsqueda de cliente
    
    // Limpiar filtros de montos
    setMinVentas(0)                  // 💵 Monto mínimo en 0
    setMaxVentas(0)                  // 💵 Monto máximo en 0
    
    // Limpiar filtros de estado
    setMostrarDeletes(false)         // 🗑️ No mostrar eliminados
    setIdTransaccionSearch("")       // 🔍 Sin búsqueda por ID

    // Restaurar fechas a rango default (últimos 30 días)
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))  // 📅 30 días atrás
    setEndDate(today)                        // 📅 Hoy

    // Limpiar filtros de tipo y canal
    setTipoTransaccionFiltro("todos")        // 📋 Todos los tipos
    setSelectedCanalEntidad(null)            // 🏪 Sin filtro de canal
    
    // Limpiar filtros de medios de pago
    setSelectedTipoMedioDePago(null)         // 💳 Sin filtro de tipo medio de pago
    setSelectedMedioDePago(null)             // 💳 Sin filtro de medio de pago específico
    
    // Limpiar atributos personalizados
    setAtributosEntidadFiltro({})            // 🏷️ Sin atributos personalizados
    
    // Ocultar paneles de filtros expandidos
    setMostrarFiltroMontos(false)
    setMostrarFiltroCliente(false)
    setMostrarFiltroMediosPago(false)
    
    // 🔄 Recargar datos con valores por defecto
    // Nota: Usa startDate actual (antes de actualizarlo) porque setStartDate es asíncrono
    // El useEffect de startDate se encargará de recargar cuando se actualice el estado
    fetchData(startDate, 1, endDate, null, null, 0, 0, false, null, null, null, 100, {})
  }

  // Función para renderizar el contenido de los filtros (reutilizable)
  const renderFiltrosContent = () => (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: isLaptopDown ? "column" : "row",
        }}
      >
        {/* Filtro de fechas - PRIMERA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: isLaptopDown ? "100%" : 250, width: isLaptopDown ? "100%" : "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Botón para retroceder rango de fechas */}
            <IconButton onClick={decreaseDate} color="primary" size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            
            {/* 
              📅 DATEPICKER "DESDE" - Filtro de Fecha Inicial
              
              - Reactividad automática: Al cambiar este campo, el useEffect de startDate
                recarga automáticamente los datos con todos los filtros aplicados
              - Valor inicial: Últimos 30 días (today.subtract(30, "day"))
              - Formato: DD/MM/YYYY (⚠️ hardcoded, ver sección formatoFechas en documentación)
              - onChange: Actualiza el estado startDate, disparando el useEffect
              
              📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md
            */}
            <DatePicker
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"  // ⚠️ Formato hardcoded - pendiente implementar formatoFechasGlobal
              label="Desde"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
            
            {/* 
              📅 DATEPICKER "HASTA" - Filtro de Fecha Final
              
              - Reactividad manual: Cambiar este campo NO recarga automáticamente
              - Usuario debe hacer clic en "Buscar" para aplicar cambios de fecha "hasta"
              - Valor inicial: Hoy (today)
              - Formato: DD/MM/YYYY (⚠️ hardcoded)
              - onChange: Actualiza el estado endDate
              - Se normaliza con timezone en fetchData usando getFechaHoraActualClient
              
              💡 Diferencia con "Desde": Este filtro requiere clic en "Buscar" para aplicarse
              
              📖 Ver documentación: FILTROS-FECHAS-Y-TIMEZONE.md
            */}
            <DatePicker
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"  // ⚠️ Formato hardcoded - pendiente implementar formatoFechasGlobal
              label="Hasta"
              slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 }, fullWidth: isLaptopDown } }}
              sx={{ flex: 1 }}
            />
            <IconButton onClick={increaseDate} color="primary" size="small">
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </LocalizationProvider>
        </Box>

        {/* Filtro de ID, ubicación y cliente - SEGUNDA PRIORIDAD */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: isLaptopDown ? "100%" : 300, width: isLaptopDown ? "100%" : "auto", flexDirection: isLaptopDown ? "column" : "row" }}>
          <TextField
            label="Buscar por ID"
            type="number"
            size="small"
            value={idTransaccionSearch}
            onChange={(e) => setIdTransaccionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: isLaptopDown ? "100%" : "20%" }}
            fullWidth={isLaptopDown}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={ubicaciones.map((it) => ({ id: it.id, label: it.descripcion }))}
            value={selectedUbicacion}
            onChange={(event, newValue) => setSelectedUbicacion(newValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Ubicación" variant="outlined" />}
          />
          <Autocomplete
            size="small"
            disablePortal
            options={entidadesClientesTresLetras.map((it) => ({
              label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
              ...it,
            }))}
            value={dniCliente}
            onChange={(event, newValue) => {
              setDniCliente(newValue || null)
            }}
            onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
            sx={{ flex: 1, width: isLaptopDown ? "100%" : "auto" }}
            renderInput={(params) => <TextField {...params} label="Cliente - DNI / CUIT / CUIL" variant="outlined" />}
          />
        </Box>

        {/* Botones de acción (solo desktop) */}
        {!isLaptopDown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                padding: "8px 16px",
                color: "white",
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenGraficasModal}
              disabled={loading}
              startIcon={<BarChartIcon />}
              sx={{
                borderColor: "rgb(244, 177, 14)",
                color: "rgb(244, 177, 14)",
                textTransform: "none",
                padding: "8px 16px",
                "&:hover": {
                  borderColor: alpha("rgb(244, 177, 14)", 0.8),
                  backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                },
              }}
            >
              Gráficas
            </Button>

            <Tooltip title="Descargar PDF">
              <IconButton
                onClick={generarPDFVentas}
                disabled={loading || !sortedData || sortedData.length === 0}
                size="small"
                sx={{
                  bgcolor: alpha("#d32f2f", 0.1),
                  "&:hover": { bgcolor: alpha("#d32f2f", 0.2) },
                  "&:disabled": { bgcolor: alpha("#d32f2f", 0.05) },
                }}
              >
                <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
              <IconButton
                onClick={resetFilters}
                color="primary"
                size="small"
                sx={{
                  bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={sidebarOpen ? "Cerrar Más Filtros" : "Mostrar Más Filtros"}>
              <Badge 
                badgeContent={
                  (minVentas > 0 || maxVentas > 0 ? 1 : 0) +
                  (selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos" || selectedCanalEntidad ? 1 : 0) +
                  (Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0)
                } 
                color="secondary"
              >
              <IconButton
                  onClick={toggleSidebar}
                color="primary"
                size="small"
                sx={{
                    bgcolor: sidebarOpen ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                  "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                }}
              >
                <TuneIcon />
              </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Boxes de filtros activos (debajo del menú principal) */}
      {!isLaptopDown && (minVentas > 0 || maxVentas > 0 || dniCliente || selectedUbicacion || selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos" || selectedCanalEntidad || Object.values(atributosEntidadFiltro).some(v => v)) && (
        <Box
          sx={{
            px: 0,
            pt: 2,
            pb: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, color: theme.palette.text.secondary }}>
            Filtros activos:
          </Typography>
          
          {/* Chip de Montos */}
          {(minVentas > 0 || maxVentas > 0) && (
            <Chip
              label={`Montos: ${minVentas > 0 ? `$${minVentas.toLocaleString("es-AR")}` : "Sin mínimo"} - ${maxVentas > 0 ? `$${maxVentas.toLocaleString("es-AR")}` : "Sin máximo"}`}
              size="small"
              onDelete={() => {
                setMinVentas(0)
                setMaxVentas(0)
                setMostrarFiltroMontos(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Cliente */}
          {dniCliente && (
            <Chip
              label={`Cliente: ${dniCliente.descripcion || dniCliente.label || dniCliente.dniCuitCuil || ""}`}
              size="small"
              onDelete={() => {
                setDniCliente(null)
                setSearchTextCliente("")
                setMostrarFiltroCliente(false)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Ubicación */}
          {selectedUbicacion && (
            <Chip
              label={`Ubicación: ${selectedUbicacion.label || selectedUbicacion.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedUbicacion(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Tipo de Transacción */}
          {tipoTransaccionFiltro !== "todos" && (
            <Chip
              label={`Tipo: ${tipoTransaccionFiltro === "ventas" ? "Solo Ventas" : "Solo Devoluciones"}`}
              size="small"
              onDelete={() => {
                setTipoTransaccionFiltro("todos")
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Canal */}
          {selectedCanalEntidad && (
            <Chip
              label={`Canal: ${selectedCanalEntidad.label || selectedCanalEntidad.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedCanalEntidad(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Tipo Medio de Pago */}
          {selectedTipoMedioDePago && (
            <Chip
              label={`Tipo Pago: ${selectedTipoMedioDePago.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedTipoMedioDePago(null)
                setSelectedMedioDePago(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chip de Medio de Pago */}
          {selectedMedioDePago && (
            <Chip
              label={`Medio: ${selectedMedioDePago.descripcion || ""}`}
              size="small"
              onDelete={() => {
                setSelectedMedioDePago(null)
                buttonFiltrado()
              }}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}

          {/* Chips de Atributos de Entidad */}
          {entidadAtributoNoEliminados && entidadAtributoNoEliminados.length > 0 && entidadAtributoNoEliminados.map((atributo, index) => {
            const atributoNumero = index + 1
            const valorAtributo = atributosEntidadFiltro[atributoNumero]
            if (!valorAtributo) return null
            
            const opciones = opcionesPorAtributo[atributo.id] || []
            const opcionSeleccionada = opciones.find(o => o.id === valorAtributo)
            
            return (
              <Chip
                key={atributo.id}
                label={`${atributo.descripcion}: ${opcionSeleccionada?.descripcion || "N/A"}`}
                size="small"
                onDelete={() => {
                  setAtributosEntidadFiltro(prev => {
                    const newAtributos = { ...prev }
                    delete newAtributos[atributoNumero]
                    return newAtributos
                  })
                  buttonFiltrado()
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            )
          })}
        </Box>
      )}

      {/* Boxes de filtros expandibles (debajo del menú principal) */}
      {!isLaptopDown && (mostrarFiltroMontos || mostrarFiltroCliente || mostrarFiltroMediosPago) && (
        <Box
          sx={{
            px: 0,
            pt: 1,
            pb: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 1,
          }}
        >
          {/* Subbox de Montos */}
          {mostrarFiltroMontos && (
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <AttachMoneyIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Rango de Montos
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroMontos(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, alignItems: "center" }}>
                <TextField
                  label="Mínimo"
                  type="number"
                  size="small"
                  value={minVentas}
                  onChange={handleMinChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
                <TextField
                  label="Máximo"
                  type="number"
                  size="small"
                  value={maxVentas}
                  onChange={handleMaxChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "fit-content" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={buttonFiltrado}
                    startIcon={<SearchIcon />}
                    sx={{
                      backgroundColor: "#5b5b5f",
                      textTransform: "none",
                      padding: "8px 16px",
                      color: "white",
                      "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                    }}
                  >
                    Aplicar
                  </Button>
                  {(minVentas > 0 || maxVentas > 0) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setMinVentas(0)
                        setMaxVentas(0)
                        buttonFiltrado()
                      }}
                      sx={{ 
                        textTransform: "none",
                        color: "#5b5b5f",
                        borderColor: "#5b5b5f",
                        padding: "8px 16px",
                        "&:hover": {
                          borderColor: "rgb(244, 177, 14)",
                          backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                        },
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          {/* Subbox de Atributos de Entidad */}
          {mostrarFiltroCliente && (
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  Filtrar por Atributos de Entidad
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroCliente(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Filtros por Atributos de Entidad */}
              <Box sx={{ mb: 1 }}>
                {(() => {
                  const tieneAtributos = entidadAtributoNoEliminados && Array.isArray(entidadAtributoNoEliminados) && entidadAtributoNoEliminados.length > 0
                  // console.log("Renderizando filtros de atributos - tieneAtributos:", tieneAtributos, "entidadAtributoNoEliminados:", entidadAtributoNoEliminados)
                  
                  if (!tieneAtributos) {
                    return (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}>
                        No hay atributos de entidad configurados. Ve a Administración → Atributos de Entidad para configurarlos.
                      </Typography>
                    )
                  }
                  
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      {entidadAtributoNoEliminados.map((atributo, index) => {
                        const atributoNumero = index + 1
                        const atributoId = atributo.idEntidadAtributo || atributo.id
                        const opciones = opcionesPorAtributo[atributoId] || []
                        
                        return (
                          <Autocomplete
                            key={atributoId}
                            size="small"
                            disablePortal
                            options={opciones}
                            getOptionLabel={(option) => option.descripcion || ""}
                            value={opciones.find(o => o.id === atributosEntidadFiltro[atributoNumero]) || null}
                            onChange={(event, newValue) => {
                              setAtributosEntidadFiltro(prev => {
                                const newAtributos = { ...prev }
                                if (newValue) {
                                  newAtributos[atributoNumero] = newValue.id
                                } else {
                                  delete newAtributos[atributoNumero]
                                }
                                return newAtributos
                              })
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={atributo.descripcion}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <PersonIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: atributosEntidadFiltro[atributoNumero]
                                      ? alpha(theme.palette.secondary.main, 0.05)
                                      : "white",
                                  },
                                }}
                              />
                            )}
                          />
                        )
                      })}
                      {/* Botones de Acción */}
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={buttonFiltrado}
                          startIcon={<SearchIcon />}
                          sx={{
                            backgroundColor: "#5b5b5f",
                            textTransform: "none",
                            padding: "8px 16px",
                            color: "white",
                            "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                          }}
                        >
                          Aplicar
                        </Button>
                        {Object.values(atributosEntidadFiltro).filter(v => v).length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setAtributosEntidadFiltro({})
                              buttonFiltrado()
                            }}
                            sx={{ 
                              textTransform: "none",
                              color: "#5b5b5f",
                              borderColor: "#5b5b5f",
                              padding: "8px 16px",
                              "&:hover": {
                                borderColor: "rgb(244, 177, 14)",
                                backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                              },
                            }}
                          >
                            Limpiar
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )
                })()}
              </Box>
            </Paper>
          )}

          {/* Subbox de Medios de Pago y Canal */}
          {mostrarFiltroMediosPago && (
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, color: "#5b5b5f" }}>
                  <PointOfSaleIcon fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
                  {canalesEntidades && canalesEntidades.length > 0 ? "Medios de Pago y Canal" : "Medios de Pago"}
                </Typography>
                <IconButton size="small" onClick={() => setMostrarFiltroMediosPago(false)} sx={{ color: "#5b5b5f" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, alignItems: "center" }}>
                {/* Tipo de Transacción */}
                <Autocomplete
                  size="small"
                  disablePortal
                  options={[
                    { id: "todos", label: "Todos" },
                    { id: "ventas", label: "Solo Ventas" },
                    { id: "devoluciones", label: "Solo Devoluciones" },
                  ]}
                  value={{
                    id: tipoTransaccionFiltro,
                    label:
                      tipoTransaccionFiltro === "todos"
                        ? "Todos"
                        : tipoTransaccionFiltro === "ventas"
                        ? "Solo Ventas"
                        : "Solo Devoluciones",
                  }}
                  onChange={(event, newValue) => {
                    setTipoTransaccionFiltro(newValue?.id || "todos")
                  }}
                  renderInput={(params) => <TextField {...params} label="Tipo de Transacción" variant="outlined" size="small" />}
                />

                {/* Tipo Medio de Pago */}
                <Autocomplete
                  size="small"
                  disablePortal
                  options={tipoMedioDePago}
                  getOptionLabel={(option) => option.descripcion || ""}
                  value={selectedTipoMedioDePago}
                  onChange={(event, newValue) => {
                    setSelectedTipoMedioDePago(newValue)
                    if (!newValue) {
                      setSelectedMedioDePago(null)
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Tipo de Medio de Pago" variant="outlined" size="small" />}
                />
                
                {/* Medio de Pago específico */}
                <Autocomplete
                  size="small"
                  disablePortal
                  options={medioDePagoIdTipoEspecifico}
                  getOptionLabel={(option) => option.descripcion || ""}
                  value={selectedMedioDePago}
                  onChange={(event, newValue) => {
                    setSelectedMedioDePago(newValue)
                  }}
                  disabled={!selectedTipoMedioDePago}
                  renderInput={(params) => <TextField {...params} label="Medio de Pago Específico" variant="outlined" size="small" />}
                />

                {/* Canal */}
                {canalesEntidades && canalesEntidades.length > 0 ? (
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={canalesEntidades}
                    getOptionLabel={(option) => option.descripcion || ""}
                    value={selectedCanalEntidad}
                    onChange={(event, newValue) => {
                      setSelectedCanalEntidad(newValue)
                    }}
                    renderInput={(params) => <TextField {...params} label="Canal de Entidad" variant="outlined" size="small" />}
                  />
                ) : (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={buttonFiltrado}
                      startIcon={<SearchIcon />}
                      sx={{
                        backgroundColor: "#5b5b5f",
                        textTransform: "none",
                        padding: "8px 16px",
                        color: "white",
                        "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                      }}
                    >
                      Aplicar
                    </Button>
                    {(selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos") && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setTipoTransaccionFiltro("todos")
                          setSelectedTipoMedioDePago(null)
                          setSelectedMedioDePago(null)
                          buttonFiltrado()
                        }}
                        sx={{ 
                          textTransform: "none",
                          color: "#5b5b5f",
                          borderColor: "#5b5b5f",
                          padding: "8px 16px",
                          "&:hover": {
                            borderColor: "rgb(244, 177, 14)",
                            backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                          },
                        }}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Box>
                )}

                {/* Botones de Acción - Solo si hay canal */}
                {canalesEntidades && canalesEntidades.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={buttonFiltrado}
                      startIcon={<SearchIcon />}
                      sx={{
                        backgroundColor: "#5b5b5f",
                        textTransform: "none",
                        padding: "8px 16px",
                        color: "white",
                        "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                      }}
                    >
                      Aplicar
                    </Button>
                    {(selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos" || selectedCanalEntidad) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setTipoTransaccionFiltro("todos")
                          setSelectedTipoMedioDePago(null)
                          setSelectedMedioDePago(null)
                          setSelectedCanalEntidad(null)
                          buttonFiltrado()
                        }}
                        sx={{ 
                          textTransform: "none",
                          color: "#5b5b5f",
                          borderColor: "#5b5b5f",
                          padding: "8px 16px",
                          "&:hover": {
                            borderColor: "rgb(244, 177, 14)",
                            backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                          },
                        }}
                      >
                        Limpiar
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          )}

        </Box>
      )}
    </>
  )

  return (
    <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      width: "100%",
      maxWidth: "100%",
      backgroundColor: "#f5f5f5",
      position: "relative",
    }}
  >
      <ThemeProvider theme={theme}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
                minWidth: 300,
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {loadingMessage}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Contenedor de filtros para Desktop (siempre visible) */}
        {!isLaptopDown && (
          <Collapse in={true} timeout={250}>
            <Paper
              className="diseñoFiltroTransaccionesVenta"
              elevation={3}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                maxHeight: "40vh",
                overflowX: "hidden",
                overflowY: "auto",
                flexShrink: 0,
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.6),
                  },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  borderRadius: "4px",
                },
              }}
            >
              {renderFiltrosContent()}
            </Paper>
          </Collapse>
        )}

        {/* Menú lateral (solo desktop) */}
        {!isLaptopDown && (
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="persistent"
            sx={{
              "& .MuiDrawer-paper": {
                width: 80,
                top: 0,
                height: "100%",
                maxHeight: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.98),
                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
                zIndex: 1200,
                overflowX: "hidden",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 1 }}>
              {/* Botón de cerrar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <IconButton size="small" onClick={toggleSidebar}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* 1. Atributos de Entidad */}
                <Tooltip title="Filtros por Atributos de Entidad" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('cliente')}
                    sx={{
                      color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroCliente || Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroCliente ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={Object.values(atributosEntidadFiltro).filter(v => v).length > 0 ? 1 : 0} 
                      color="secondary"
                    >
                      <PersonIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 2. Medios de Pago */}
                <Tooltip title={canalesEntidades && canalesEntidades.length > 0 ? "Filtros de Medios de Pago y Canal" : "Filtros de Medios de Pago"} placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('mediosPago')}
                    sx={{
                      color: mostrarFiltroMediosPago || selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos" || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroMediosPago || selectedTipoMedioDePago || selectedMedioDePago || tipoTransaccionFiltro !== "todos" || selectedCanalEntidad ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroMediosPago ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge 
                      badgeContent={
                        (selectedTipoMedioDePago ? 1 : 0) + 
                        (selectedMedioDePago ? 1 : 0) + 
                        (tipoTransaccionFiltro !== "todos" ? 1 : 0) +
                        (selectedCanalEntidad && canalesEntidades && canalesEntidades.length > 0 ? 1 : 0)
                      } 
                      color="secondary"
                    >
                      <PointOfSaleIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 3. Montos */}
                <Tooltip title="Filtros de Montos" placement="left">
                  <IconButton
                    onClick={() => toggleFiltro('montos')}
                    sx={{
                      color: mostrarFiltroMontos || minVentas > 0 || maxVentas > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      "& svg": {
                        color: mostrarFiltroMontos || minVentas > 0 || maxVentas > 0 ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                      },
                      bgcolor: mostrarFiltroMontos ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                      "&:hover": {
                        bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                      },
                    }}
                  >
                    <Badge badgeContent={minVentas > 0 || maxVentas > 0 ? 1 : 0} color="secondary">
                      <AttachMoneyIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* 4. Mostrar Eliminados - Solo si tiene permiso verVentasEliminadas */}
                {tieneAccesoFuncionalidad(user, 'VENTAS2', 'verVentasEliminadas') && (
                  <Tooltip title={mostrarDeletes ? "Ocultar eliminados" : "Mostrar eliminados"} placement="left">
                    <IconButton
                      onClick={() => {
                        const nuevoEstado = !mostrarDeletes
                        setMostrarDeletes(nuevoEstado)
                        // Llamar a fetchData directamente con el nuevo estado para evitar problemas de sincronización
                        fetchData(
                          startDate,
                          page,
                          endDate,
                          dniCliente,
                          selectedUbicacion,
                          minVentas,
                          maxVentas,
                          nuevoEstado,
                          idTransaccionSearch,
                          tipoTransaccionFiltro,
                          selectedCanalEntidad,
                          100,
                          atributosEntidadFiltro,
                        )
                      }}
                      sx={{
                        color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        "& svg": {
                          color: mostrarDeletes ? "rgb(244, 177, 14)" : "rgba(0, 0, 0, 0.54) !important",
                        },
                        bgcolor: mostrarDeletes ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                        "&:hover": {
                          bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Divider sx={{ my: 1 }} />

              </Box>
            </Box>
          </Drawer>
        )}

        {/* Contenedor de la tabla (scrollable siempre dentro de la tarjeta) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            minHeight: 0,
            minWidth: 0,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={sortedData}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
            getRowHeight={() => (isLaptopDown ? 40 : 35)}
            headerHeight={isLaptopDown ? 40 : 45}
            getRowClassName={(params) => (params.row?.idTipoTransaccionID === 6 ? "row-devolucion" : "")}
            sx={{
              width: "100%",
              border: 0,
              // --- estilos de tabla responsive ---
              "& .MuiDataGrid-footerContainer": { display: "none !important" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: isLaptopDown ? "40px !important" : "45px !important",
                minHeight: isLaptopDown ? "40px !important" : "45px !important",
                maxHeight: isLaptopDown ? "40px !important" : "45px !important",
                padding: isLaptopDown ? "0 8px" : "0 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                height: isLaptopDown ? "40px !important" : "45px !important",
                minHeight: isLaptopDown ? "40px !important" : "45px !important",
                maxHeight: isLaptopDown ? "40px !important" : "45px !important",
                padding: isLaptopDown ? "0 4px" : "0 8px",
              },
              "& .MuiDataGrid-columnHeaderTitle": { 
                color: "white !important", 
                lineHeight: isLaptopDown ? "40px !important" : "45px !important",
                fontSize: isLaptopDown ? "0.75rem" : "0.875rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": { color: "white !important" },
              "& .MuiDataGrid-menuIconButton": { color: "rgb(244, 177, 14) !important" },
              "& .MuiInputBase-root": { color: "rgb(244, 177, 14) !important" },
              "& .MuiTablePagination-root": { color: "black !important" },
              "& .MuiSelect-icon": { color: "rgb(244, 177, 14) !important" },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isLaptopDown ? "0 4px" : "0 16px",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
                "&:hover": { backgroundColor: "#E3AFD !important", transition: "background 0.3s ease" },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": { backgroundColor: alpha("#9e9e9e !important", 0.1) },
              },
              "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important" },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: isLaptopDown ? "4px" : "2px" },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "10px" },
            }}
            components={{ Pagination: () => null }}
          />
        </Paper>

        {/* Footer (paginación) */}
        <Box 
          sx={{ 
            flexShrink: 0, 
            p: isLaptopDown ? 0.5 : 1,
            backgroundColor: "white",
            position: "relative",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaVentas"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        {/* Modales */}
        {openModal === true ? (
          <ModalViewTablaVenta
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
          />
        ) : null}

        {openModalDevolucion === true ? (
          <ModalViewTablaDevolucion
            openModal={openModalDevolucion}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
            selectedTransaccionEntidad={selectedTransaccionEntidad}
            transaccionItems={transaccionItems}
          />
        ) : null}

        <GraficasVentasModal 
          open={openGraficasModal} // abrir modal de graficas
          handleClose={handleCloseGraficasModal} // cerrar modal de graficas
          data={datosGraficas} // datos para graficas
          loading={loadingGraficas} // estado de carga para graficas
        />

        {/* Modal de Eliminación con MUI */}
        <Dialog
          open={openDeleteModal}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'visible',
            }
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              pt: 3,
              px: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha('#dc3545', 0.1),
                flexShrink: 0,
              }}
            >
              <DeleteIcon sx={{ color: '#dc3545', fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Eliminar Transacción
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Transacción #{transaccionToDelete?.id}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCancelDelete}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.05),
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
            <Stack spacing={2.5}>
              {/* Alerta de Advertencia */}
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                sx={{
                  borderRadius: 2,
                  backgroundColor: alpha('#f4b10e', 0.1),
                  border: '1px solid',
                  borderColor: alpha('#f4b10e', 0.3),
                  '& .MuiAlert-icon': {
                    color: '#f4b10e',
                  },
                }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Atención: Acción irreversible
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Esta acción no se puede deshacer y quedará registrada en el historial de auditoría.
                </Typography>
              </Alert>

              {/* Campo de Motivo */}
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.primary"
                  sx={{
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                  Motivo de la eliminación
                  <Typography component="span" sx={{ color: '#dc3545', ml: 0.5 }}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  placeholder="Describe el motivo por el cual se elimina esta transacción... *"
                  value={deleteComentario}
                  onChange={(e) => setDeleteComentario(e.target.value)}
                  autoFocus
                  error={deleteComentario.trim() === '' && deleteComentario !== ''}
                  helperText={
                    deleteComentario.trim() === '' && deleteComentario !== ''
                      ? 'El motivo de eliminación es obligatorio'
                      : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      '&:hover fieldset': {
                        borderColor: '#f4b10e',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#f4b10e',
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Este comentario quedará registrado en el historial de auditoría
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              gap: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: alpha('#000', 0.02),
            }}
          >
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderColor: alpha('#6c757d', 0.3),
                color: '#6c757d',
                '&:hover': {
                  borderColor: '#6c757d',
                  backgroundColor: alpha('#6c757d', 0.05),
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              startIcon={<DeleteIcon />}
              disabled={!deleteComentario.trim()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                backgroundColor: '#dc3545',
                '&:hover': {
                  backgroundColor: '#c82333',
                },
                '&.Mui-disabled': {
                  backgroundColor: alpha('#dc3545', 0.3),
                  color: alpha('#fff', 0.5),
                },
              }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Box>
  )
}

export default TablaVentasComponent
